import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import StreamingVideo from './StreamingVideo';

const LazyVideo = ({
  src,
  className = '',
  style = {},
  autoPlay = true,
  muted = true,
  loop = true,
  playsInline = true,
  loadDelay = 2000, // Delay before starting video load
  onLoadStart = null,
  onCanPlay = null,
  ...props
}) => {
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [error, setError] = useState(false);
  const loadTimerRef = useRef();

  useEffect(() => {
    // Simple approach: always start loading after a delay
    if (loadDelay === 0) {
      setShouldLoad(true);
      setIsLoading(true);
      if (onLoadStart) {
        onLoadStart();
      }
      return;
    }

    loadTimerRef.current = setTimeout(() => {
      setShouldLoad(true);
      setIsLoading(true);
      if (onLoadStart) {
        onLoadStart();
      }
    }, loadDelay);

    return () => {
      if (loadTimerRef.current) {
        clearTimeout(loadTimerRef.current);
      }
    };
  }, [loadDelay, src, onLoadStart]);

  const handleVideoLoadProgress = (progress) => {
    if (progress >= 50) { // Start showing video when 50% loaded
      setIsLoading(false);
      setVideoLoaded(true);
      if (onCanPlay) {
        onCanPlay();
      }
    }
  };

  const handleVideoError = (error) => {
    setError(true);
    setIsLoading(false);
  };

  return (
    <div className={`relative overflow-hidden ${className}`} style={style}>
      {/* Loading placeholder */}
      {!shouldLoad && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black flex items-center justify-center">
          <div className="text-center text-white/70">
            <div className="w-16 h-16 border-4 border-primary-400/30 border-t-primary-400 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-sm">Preparing background...</p>
            <p className="text-xs text-gray-500 mt-2">{loadDelay/1000}s delay</p>
          </div>
        </div>
      )}

      {/* Loading state */}
      {shouldLoad && isLoading && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black flex items-center justify-center z-10">
          <div className="text-center text-white">
            <div className="w-20 h-20 border-4 border-primary-400/30 border-t-primary-400 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-base font-medium">Loading background video...</p>
            <p className="text-sm text-gray-400 mt-2">This may take a moment</p>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black flex items-center justify-center">
          <div className="text-center text-white">
            <div className="w-12 h-12 text-red-400 mx-auto mb-3">
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="text-sm">Background unavailable</p>
            <p className="text-xs text-gray-500 mt-1">Check console for details</p>
          </div>
        </div>
      )}

      {/* StreamingVideo component - only loads when shouldLoad is true */}
      {shouldLoad && !error && (
        <div className="w-full h-full">
          <StreamingVideo
            src={src}
            className={className}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 1,
            }}
            autoPlay={autoPlay}
            muted={muted}
            loop={loop}
            playsInline={playsInline}
            showProgress={true}
            useChunkedLoading={false}
            onLoadProgress={handleVideoLoadProgress}
            onError={handleVideoError}
          />
        </div>
      )}

      {/* Fallback gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black" style={{ zIndex: 0 }} />
    </div>
  );
};

export default LazyVideo;